import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  VStack,
  Heading,

  Text,
  Flex,
  IconButton,
  Image,
  chakra,
} from "@chakra-ui/react";
import {
  PhoneIcon,
  EmailIcon,
  LockIcon,
  EditIcon,
  ViewIcon,
  ViewOffIcon,
} from "@chakra-ui/icons";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import PhoneInput, { Value } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { keyframes } from "@emotion/react";
import { showToast } from "../utils/toastUtils";

const gradientMove = keyframes`
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 100% 50%;
  }
`;

const blob = keyframes` 
  0% {
    transform: scale(1);
  }
  100% {
    transform: scale(1.3); // Adjusted scale slightly for more visible movement
  }
`;

const float = keyframes` 
  0% {
    transform: translateY(0px);
  }
  100% {
    transform: translateY(-20px); // Adjusted float distance for more visible movement
  }
`;

const Register: React.FC = () => {
  const { signUp } = useAuth(); // UseAuth hook
  const [newUser, setNewUser] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
  });
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const handleInputChange = (field: string, value: string) => {
    setNewUser((prev) => ({ ...prev, [field]: value }));
  };

  const handleRegister = async () => {
    // Validation
    if (
      !newUser.firstName ||
      !newUser.lastName ||
      !newUser.email ||
      !newUser.phone ||
      !password ||
      !confirmPassword
    ) {
      showToast({
        title: "Error",
        description: "All fields are required.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (password !== confirmPassword) {
      showToast({
        title: "Error",
        description: "Passwords do not match.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    // if (!/^\d{10}$/.test(newUser.phone)) {
    //   showToast({
    //     title: "Error",
    //     description: "Invalid phone number. Must be 10 digits.",
    //     status: "error",
    //     duration: 3000,
    //     isClosable: true,
    //   });
    //   return;
    // }

    try {
      setLoading(true);

      // Register user
      const result = await signUp(newUser.email, password, {
        firstName: newUser.firstName,
        lastName: newUser.lastName,
        phone: newUser.phone,
      });

      if (!result || !result.user) {
        throw new Error("User registration failed.");
      }
      console.log("result", result);
      // Navigate to the organization creation page
      showToast({
        title: "Success",
        description:
          "Registration successful! Proceed to create an organization.",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      navigate("/create-organization");
    } catch (error: any) {
      console.error("Error during registration:", error);
      showToast({
        title: "Error",
        description: error.message || "Registration failed.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex height="100vh" width="100%" overflow="hidden" position="relative">
      {/* Left Form Section */}
      <Box
        flex="1"
        display={{ base: "flex", md: "flex" }} // Show form on all screen sizes
        alignItems="center"
        justifyContent="center"
        bg="gray.50"
        zIndex="1"
        position="relative" // Set position to relative for absolute children
        width={{ base: "100%", md: "50%" }} // Form takes full width on mobile, half on desktop
      >
        {/* Balloon-like elements positioned within the left section */}
        <Box
          position="absolute"
          top="-100px"
          right="-100px"
          w="300px"
          h="300px"
          borderRadius="full"
          bg="rgba(26, 28, 31, 0.06)"
          zIndex="0"
          display={{ base: "none", md: "block" }} // Hidden on mobile
        />
        <Box
          position="absolute"
          bottom="-50px"
          left="-50px"
          w="200px"
          h="200px"
          borderRadius="full"
          bg="rgba(58, 69, 83, 0.08)"
          zIndex="0"
          display={{ base: "none", md: "block" }} // Hidden on mobile
        />

        {/* The form card remains on top */}
        <Box
          width={{ base: "90%", md: "90%" }} // Card width on different screen sizes
          maxW="500px"
          p={8}
          bg="white"
          borderRadius="xl"
          boxShadow="0 4px 6px rgba(0, 0, 0, 0.1)"
          zIndex="1" // Ensure the form card is above the balloons
        >
          <Box textAlign="center" mb={3}>
            <Text
              as="span"
              fontFamily="Roboto, sans-serif"
              fontSize="2xl"
              fontWeight="700"
              textStyle="goldGradient"
              display="inline"
            >
              Admin
            </Text>
            <Text
              as="span"
              fontFamily="Roboto, sans-serif"
              fontSize="2xl"
              fontWeight="700"
              color="black"
            >
              {" "}
              Registration
            </Text>
          </Box>

          <VStack spacing={1} fontSize={"sm"}>
            {/* First Name */}
            <FormControl isRequired>
              <FormLabel fontSize={"xs"} color="zs.accent">
                First Name
              </FormLabel>
              <InputGroup color={"black"} fontSize={"xs"}>
                <Input
                  color={"black"}
                  fontSize={"xs"}
                  placeholder="Enter First Name"
                  value={newUser.firstName}
                  onChange={(e) =>
                    handleInputChange("firstName", e.target.value)
                  }
                  borderRadius="full"
                  _focus={{
                    borderColor: "zs.accent",
                    boxShadow: "0 0 0 1px var(--chakra-colors-zs-accent)",
                    outline: "none",
                  }}
                  _hover={{
                    borderColor: "zs.accent",
                  }}
                  _focusVisible={{
                    boxShadow: "0 0 0 1px var(--chakra-colors-zs-accent)",
                    outline: "none",
                  }}
                />
                <InputRightElement color="zs.accent">
                  <EditIcon />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            {/* Last Name */}
            <FormControl isRequired>
              <FormLabel fontSize={"xs"} color="zs.accent">
                Last Name
              </FormLabel>
              <InputGroup>
                <Input
                  color={"black"}
                  fontSize={"xs"}
                  placeholder="Enter Last Name"
                  value={newUser.lastName}
                  onChange={(e) =>
                    handleInputChange("lastName", e.target.value)
                  }
                  borderRadius="full"
                  _focus={{
                    borderColor: "zs.accent",
                    boxShadow: "0 0 0 1px var(--chakra-colors-zs-accent)",
                    outline: "none",
                  }}
                  _hover={{
                    borderColor: "zs.accent",
                  }}
                  _focusVisible={{
                    boxShadow: "0 0 0 1px var(--chakra-colors-zs-accent)",
                    outline: "none",
                  }}
                />
                <InputRightElement color="zs.accent">
                  <EditIcon />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            {/* Email */}
            <FormControl isRequired>
              <FormLabel fontSize={"xs"} color="zs.accent">
                Email
              </FormLabel>
              <InputGroup>
                <Input
                  color={"black"}
                  fontSize={"xs"}
                  type="email"
                  placeholder="Enter Email"
                  value={newUser.email}
                  onChange={(e) => handleInputChange("email", e.target.value)}
                  borderRadius="full"
                  _focus={{
                    borderColor: "zs.accent",
                    boxShadow: "0 0 0 1px var(--chakra-colors-zs-accent)",
                    outline: "none",
                  }}
                  _hover={{
                    borderColor: "zs.accent",
                  }}
                  _focusVisible={{
                    boxShadow: "0 0 0 1px var(--chakra-colors-zs-accent)",
                    outline: "none",
                  }}
                />
                <InputRightElement color="zs.accent">
                  <EmailIcon />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            {/* Phone */}
            <FormControl isRequired>
              <FormLabel fontSize={"xs"} color="zs.accent">
                Phone
              </FormLabel>
              <InputGroup>
                <PhoneInput
                  color={"black"}
                  fontSize={"xs"}
                  defaultCountry="IN"
                  value={newUser.phone}
                  onChange={(phone: string) =>
                    handleInputChange("phone", phone)
                  }
                  international
                  countryCallingCodeEditable={false}
                  className="custom-phone-input-reg"
                />
                <InputRightElement top={"10px"} right={"5px"} color="zs.accent">
                  <PhoneIcon />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            {/* Password */}
            <FormControl isRequired>
              <FormLabel fontSize={"xs"} color="zs.accent">
                Password
              </FormLabel>
              <InputGroup>
                <Input
                  color={"black"}
                  fontSize={"xs"}
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  borderRadius="full"
                  _focus={{
                    borderColor: "zs.accent",
                    boxShadow: "0 0 0 1px var(--chakra-colors-zs-accent)",
                    outline: "none",
                  }}
                  _hover={{
                    borderColor: "zs.accent",
                  }}
                  _focusVisible={{
                    boxShadow: "0 0 0 1px var(--chakra-colors-zs-accent)",
                    outline: "none",
                  }}
                />
                <InputRightElement>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    color="zs.accent"
                    icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label="Toggle password visibility"
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            {/* Confirm Password */}
            <FormControl isRequired>
              <FormLabel fontSize={"xs"} color="zs.accent">
                Confirm Password
              </FormLabel>
              <InputGroup>
                <Input
                  color={"black"}
                  fontSize={"xs"}
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm Password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  borderRadius="full"
                  _focus={{
                    borderColor: "zs.accent",
                    boxShadow: "0 0 0 1px var(--chakra-colors-zs-accent)",
                    outline: "none",
                  }}
                  _hover={{
                    borderColor: "zs.accent",
                  }}
                  _focusVisible={{
                    boxShadow: "0 0 0 1px var(--chakra-colors-zs-accent)",
                    outline: "none",
                  }}
                />
                <InputRightElement>
                  <IconButton
                    size="sm"
                    variant="ghost"
                    color="zs.accent"
                    icon={showConfirmPassword ? <ViewOffIcon /> : <ViewIcon />}
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label="Toggle confirm password visibility"
                  />
                </InputRightElement>
              </InputGroup>
            </FormControl>

            {/* Register Button */}
            <Button
              fontSize={"sm"}
              mt={4}
              variant="gold"
              fontWeight="bold"
              borderRadius="full"
              width="full"
              height={"40px"}
              isLoading={loading}
              onClick={handleRegister}
            >
              Register
            </Button>

            {/* Already Have an Account */}
            <Text mt={2} fontSize="xs" color="zs.accent">
              Already have an account?{" "}
              <Text
                as="span"
                fontWeight="bold"
                cursor="pointer"
                _hover={{ textDecoration: "underline" }}
                onClick={() => navigate("/login")}
              >
                Login
              </Text>
            </Text>
          </VStack>
        </Box>
      </Box>

      {/* Right Gradient Welcome Section */}
      <Box
        flex="1"
        display={{ base: "none", md: "flex" }}
        alignItems="center"
        justifyContent="center"
        position="relative"
        overflow="hidden"
        bg="#0B0B0F"
        color="zs.text"
        width={{ base: "100%", md: "50%" }}
      >
        {/* Added Large Blob Elements (Animated) - More pronounced */}
        <Box
          position="absolute"
          top="10%"
          left="10%"
          width="200px"
          height="200px"
          bg="rgba(181, 145, 67, 0.10)"
          borderRadius="full"
          filter="blur(70px)"
          opacity={0.6}
          animation={`${blob} 8s infinite alternate`}
        />
        <Box
          position="absolute"
          bottom="10%"
          right="10%"
          width="250px"
          height="250px"
          bg="rgba(251, 210, 130, 0.08)"
          borderRadius="full"
          filter="blur(90px)"
          opacity={0.5}
          animation={`${blob} 9s infinite alternate-reverse`}
        />
        <Box
          position="absolute"
          top="40%"
          right="-50px"
          width="150px"
          height="150px"
          bg="rgba(181, 145, 67, 0.05)"
          borderRadius="full"
          filter="blur(60px)"
          opacity={0.4}
          animation={`${blob} 7s infinite alternate`}
        />

        {/* Added more subtle blob elements */}
        <Box
          position="absolute"
          top="-20px"
          left="40%"
          width="100px"
          height="100px"
          bg="rgba(251, 210, 130, 0.03)"
          borderRadius="full"
          filter="blur(50px)"
          opacity={0.3}
          animation={`${blob} 10s infinite alternate-reverse`}
        />
        <Box
          position="absolute"
          bottom="-30px"
          left="-30px"
          width="120px"
          height="120px"
          bg="rgba(181, 145, 67, 0.04)"
          borderRadius="full"
          filter="blur(55px)"
          opacity={0.35}
          animation={`${blob} 11s infinite alternate`}
        />

        {/* Adding even more blob elements */}
        <Box
          position="absolute"
          top="20%"
          right="-30px"
          width="80px"
          height="80px"
          bg="rgba(251, 210, 130, 0.02)"
          borderRadius="full"
          filter="blur(40px)"
          opacity={0.2}
          animation={`${blob} 9s infinite alternate`}
        />
        <Box
          position="absolute"
          bottom="20%"
          left="-40px"
          width="90px"
          height="90px"
          bg="rgba(181, 145, 67, 0.03)"
          borderRadius="full"
          filter="blur(45px)"
          opacity={0.25}
          animation={`${blob} 10s infinite alternate-reverse`}
        />

        {/* Welcome Text Section */}
        <VStack textAlign="center" spacing={3} zIndex={1} px={8}>
          {/* ZenSight Heading and Tagline */}
          <Box textAlign="center">
            <Text
              as="span"
              fontFamily="Roboto, sans-serif"
              fontSize="7xl"
              fontWeight="700"
              textStyle="goldGradient"
              display="inline"
            >
              Zen
            </Text>
            <Text
              as="span"
              fontFamily="Roboto, sans-serif"
              fontSize="7xl"
              fontWeight="700"
              textStyle="goldGradient"
            >
              Sight
            </Text>
            <Text
              fontSize="md"
              opacity={0.9}
              fontWeight="medium"
              letterSpacing="wide"
              color="gray.300"
              position={"relative"}
              top={"-10px"}
            >
              Your Gateway to Intelligent Insights
            </Text>
          </Box>

          {/* New Heading Structure */}
          <VStack textAlign="center" spacing={0}>
            <Text
              fontSize="4xl"
              fontWeight="extrabold"
              color="white"
              letterSpacing="normal"
            >
              Elevate Team{" "}
              <chakra.span
                as="span"
                fontSize="4xl"
                fontWeight="extrabold"
                position="relative"
                sx={{
                  background: `linear-gradient(to right, var(--chakra-colors-zs-accentSoft), var(--chakra-colors-zs-accent))`,
                  WebkitBackgroundClip: "text",
                  backgroundClip: "text",
                  color: "transparent",
                }}
              >
                Performance
              </chakra.span>
            </Text>
          </VStack>

          {/* New Tagline */}
          <Text
            fontSize="lg" // Keep size at lg
            opacity={0.9}
            fontWeight="medium"
            letterSpacing="normal"
            maxW="400px"
            color="gray.300"
          >
            All-in-one platform for KPI management, analytics, and team
            well-being. Simple, intuitive, powerful.
          </Text>
        </VStack>
      </Box>
    </Flex>
  );
};

export default Register;
